<template>
  <div class="animated fadeIn">
    <div class="browse-page-wrapper">
      <!-- Hero Section -->
      <div class="hero-section">
        <div class="hero-content">
          <h1 class="hero-title">Find Your Community</h1>
          <p class="hero-subtitle">
            Discover amazing Discord servers and join the conversation.
          </p>
        </div>
      </div>

      <!-- Filter/Search Section -->
      <div class="filter-section">
        <b-row class="mb-3">
          <b-col md="3">
            <b-form-group label="Search" label-for="search-filter">
              <b-form-input
                id="search-filter"
                v-model="searchQuery"
                placeholder="Name or description..."
                @keydown.enter="onFilterChange"
              ></b-form-input>
            </b-form-group>
          </b-col>

          <b-col md="2">
            <b-form-group label="Category" label-for="category-filter">
              <b-form-select
                id="category-filter"
                v-model="selectedCategory"
                :options="categoryOptions"
              ></b-form-select>
            </b-form-group>
          </b-col>

          <b-col md="3">
            <b-form-group label="Filter by Tags" label-for="tags-filter">
              <b-form-input
                id="tags-filter"
                v-model="tagInput"
                placeholder="Add tags (press enter)"
                @keydown.enter.prevent="addTag"
              ></b-form-input>
              <div v-if="selectedTags.length > 0" class="mt-2">
                <b-badge
                  v-for="(tag, index) in selectedTags"
                  :key="index"
                  variant="secondary"
                  class="mr-1 mb-1"
                  pill
                >
                  {{ tag }}
                  <font-awesome-icon
                    icon="times"
                    style="cursor: pointer; margin-left: 5px"
                    @click="removeTag(index)"
                  />
                </b-badge>
              </div>
            </b-form-group>
          </b-col>
          <b-col md="2">
            <b-form-group label="NSFW" label-for="nsfw-filter">
              <div class="d-flex align-items-center" style="height: 38px">
                <b-form-checkbox
                  id="nsfw-filter"
                  v-model="showNsfw"
                  switch
                  @change="onNsfwToggle"
                >
                  Show NSFW
                </b-form-checkbox>
              </div>
            </b-form-group>
          </b-col>
          <b-col md="2">
            <b-form-group label="Boosted" label-for="hide-boosted-filter">
              <div class="d-flex align-items-center" style="height: 38px">
                <b-form-checkbox
                  id="hide-boosted-filter"
                  v-model="hideBoosted"
                  switch
                  @change="onFilterChange"
                >
                  Hide Boosted
                </b-form-checkbox>
              </div>
            </b-form-group>
          </b-col>
          <b-col v-if="hasAnyFilter || isFiltered" md="2" class="d-flex align-items-end mb-3">
            <b-button
              variant="primary"
              block
              class="search-trigger-btn"
              @click="onFilterChange"
            >
              <font-awesome-icon icon="search" class="mr-2" />
              Search
            </b-button>
          </b-col>
        </b-row>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center py-5">
        <div class="sk-circle-fade">
          <div class="sk-grid-cube"></div>
          <div class="sk-grid-cube"></div>
          <div class="sk-grid-cube"></div>
          <div class="sk-grid-cube"></div>
          <div class="sk-grid-cube"></div>
          <div class="sk-grid-cube"></div>
          <div class="sk-grid-cube"></div>
          <div class="sk-grid-cube"></div>
          <div class="sk-grid-cube"></div>
        </div>
        <h3 class="mt-3">Loading servers...</h3>
      </div>

      <!-- Server Cards Grid -->
      <div v-else>
        <!-- Error State -->
        <div v-if="fetchError" class="text-center py-5">
          <h4>Something went wrong</h4>
          <p class="text-muted">Failed to load servers. Please try again.</p>
          <b-button variant="primary" @click="fetchServers(currentPage)">
            <font-awesome-icon icon="sync" style="margin-right: 5px" />
            Retry
          </b-button>
        </div>

        <b-row v-else-if="servers.length > 0" class="card-container">
          <b-col
            v-for="server in servers"
            :key="server.guild_id"
            cols="12"
            sm="6"
            lg="4"
            xl="3"
            class="mb-4"
          >
            <b-card :class="['server-card', { 'golden-hue': true }]" no-body>
              <!-- Dynamic Blurred Banner -->
              <div class="server-banner-img">
                <div
                  class="server-banner-bg"
                  :style="
                    server.icon_url
                      ? { backgroundImage: 'url(' + server.icon_url + ')' }
                      : {}
                  "
                ></div>
                <div class="server-banner-overlay"></div>
                <b-badge
                  v-if="server.nsfw"
                  variant="danger"
                  class="nsfw-banner-badge"
                >
                  NSFW
                </b-badge>
                <div class="bumps-badges-wrap">
                  <div
                    v-if="server.is_boosted"
                    class="boosted-label-badge"
                    style="cursor: pointer"
                    @click="openStoreLink"
                  >
                    <img src="/img/rocket.png" alt="" class="boosted-label-icon" />
                    Boosted
                  </div>
                  <div class="bumps-banner-badge">
                    {{ server.bumps }} Bumps
                  </div>
                </div>
              </div>

              <div class="server-card-content">
                <!-- Header: Overlapping Avatar, Name, Badges -->
                <div class="server-card-header">
                  <div class="server-avatar-wrapper">
                    <b-img
                      :src="
                        server.icon_url ||
                        'https://cdn.discordapp.com/embed/avatars/0.png'
                      "
                      alt="Server icon"
                      class="server-icon"
                      rounded="circle"
                      @error="
                        (e) =>
                          (e.target.src =
                            'https://cdn.discordapp.com/embed/avatars/0.png')
                      "
                    />
                    <div v-if="server.member_count > 0" class="avatar-member-badge">
                      <span class="member-dot"></span>
                      {{ formatMemberCount(server.member_count) }} Members
                    </div>
                  </div>
                  <div class="server-info">
                    <div class="d-flex align-items-center mb-1" style="min-width: 0;">
                      <span
                        class="server-name text-truncate"
                        :title="server.guild_name"
                        style="display: block; max-width: 100%;"
                      >
                        {{ server.guild_name }}
                      </span>
                    </div>
                    <b-badge variant="primary" class="category-badge">{{
                      server.category
                    }}</b-badge>
                  </div>
                </div>

                <!-- Description + Tags shared area -->
                <div class="server-desc-tags-wrapper">
                  <div class="server-description-area">
                    <p class="server-desc-text mb-0">
                      {{ server.description }}
                    </p>
                  </div>

                  <!-- Tags -->
                  <div class="server-tags mt-2 mb-1">
                    <b-badge
                      v-for="(tag, index) in (server.tags || []).slice(0, 5)"
                      :key="index"
                      variant="secondary"
                      class="tag-badge"
                      pill
                    >
                      {{ tag.slice(0, 10) }}
                    </b-badge>
                  </div>
                </div>

                <!-- Action Button -->
                <div class="server-action">
                  <b-button
                    block
                    variant="success"
                    class="join-btn-premium w-100"
                    @click="redirectToGuild(server.guild_id)"
                  >
                    <font-awesome-icon icon="sign-in-alt" class="mr-2" />
                    Join Server
                  </b-button>
                </div>
              </div>
            </b-card>
          </b-col>
        </b-row>

        <!-- No Results -->
        <div v-else class="text-center py-5">
          <h4>No servers found</h4>
          <p class="text-muted">Try adjusting your filters or search query</p>
        </div>

        <!-- Pagination -->
        <div v-if="totalServers > 0" class="mt-4">
          <pagination
            v-model="currentPage"
            :records="totalServers"
            :per-page="pageSize"
            :options="paginationOptions"
          ></pagination>
        </div>
      </div>
    </div>

    <!-- NSFW Warning Modal -->
    <b-modal
      v-model="showNsfwModal"
      centered
      hide-footer
      :no-enforce-focus="true"
    >
      <template slot="modal-title">
        <h4 class="smalltitle">⚠️ NSFW Content Warning</h4>
      </template>

      <div class="text-center py-3">
        <p>Enabling this will show servers that may contain:</p>
        <ul class="text-left text-muted" style="display: inline-block">
          <li>Sexually explicit content</li>
          <li>Politically sensitive material</li>
          <li>Other mature themes</li>
        </ul>
        <p class="mt-2">
          <strong
            >Viewer discretion is advised. You must be 18+ to proceed.</strong
          >
        </p>
      </div>

      <div class="d-flex justify-content-center gap-2 pb-2" style="gap: 10px">
        <b-button variant="danger" @click="confirmNsfw"
          >I understand, continue</b-button
        >
        <b-button variant="secondary" @click="revertNsfw">Cancel</b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import Pagination from "vue-pagination-2";

export default {
  name: "PublicServerListing",
  components: {
    Pagination,
  },
  data() {
    return {
      loading: true,
      fetchError: false,
      servers: [],
      currentPage: 1,
      totalServers: 0,
      pageSize: 20,
      isFiltered: false,
      selectedCategory: "",
      selectedTags: [],
      tagInput: "",
      searchQuery: "",
      searchDebounceTimer: null,
      showNsfw: false,
      showNsfwModal: false,
      hideBoosted: false,
      storeLink: null,
      categoryOptions: [
        { value: "", text: "All Categories" },
        { value: "Gaming", text: "Gaming" },
        { value: "Community", text: "Community" },
        { value: "Technology", text: "Technology" },
        { value: "Education", text: "Education" },
        { value: "Music", text: "Music" },
        { value: "Art", text: "Art" },
        { value: "Sports", text: "Sports" },
        { value: "Other", text: "Other" },
      ],
    };
  },
  created() {
    this.$Progress.start();
    this.fetchServers(1);
    this.$Progress.finish();
  },
  methods: {
    async fetchServers(page) {
      this.loading = true;
      this.fetchError = false;
      this.servers = [];
      this.totalServers = 0;

      try {
        const params = {
          page: page,
          per_page: this.pageSize,
        };

        let activeFilters = false;

        // Add filters if set
        if (this.selectedCategory) {
          params.categories = this.selectedCategory;
          activeFilters = true;
        }
        if (this.selectedTags.length > 0) {
          params.tags = this.selectedTags.join(",");
          activeFilters = true;
        }
        if (!this.showNsfw) {
          params.nsfw = "false";
        } else {
          activeFilters = true;
        }
        if (this.searchQuery.trim()) {
          params.search = this.searchQuery.trim();
          activeFilters = true;
        }
        if (this.hideBoosted) {
          params.exclude_boosted = "true";
          activeFilters = true;
        }

        this.isFiltered = activeFilters;

        const res = await this.axios.get("/api/v1/public_listings", { params });

        if (res.data.success) {
          this.servers = res.data.data;
          this.totalServers =
            res.data.total != null ? res.data.total : res.data.data.length;
          this.storeLink = res.data.store_link || this.storeLink;
        }
      } catch (error) {
        console.error("Error fetching servers:", error);
        this.fetchError = true;
      } finally {
        this.loading = false;
      }
    },

    addTag() {
      const tag = this.tagInput.trim().toLowerCase();
      if (!tag) return;

      if (this.selectedTags.includes(tag)) {
        console.error("Tag already added");
        this.tagInput = "";
        return;
      }

      this.selectedTags.push(tag);
      this.tagInput = "";
    },

    removeTag(index) {
      this.selectedTags.splice(index, 1);
    },

    onPageChange(page) {
      this.fetchServers(page);
      // Scroll to top
      window.scrollTo({ top: 0, behavior: "smooth" });
    },

    onNsfwToggle(val) {
      if (val) {
        this.$nextTick(() => {
          this.showNsfw = false;
        });
        this.showNsfwModal = true;
      } else {
        this.showNsfw = false;
      }
    },

    confirmNsfw() {
      this.showNsfwModal = false;
      this.showNsfw = true;
    },

    revertNsfw() {
      this.showNsfwModal = false;
      this.showNsfw = false;
    },

    onFilterChange() {
      // Reset to page 1 when filters change
      this.currentPage = 1;
      this.fetchServers(1);
    },

    onSearchInput() {
      // No longer automatically searching on input
    },

    openStoreLink() {
      if (!this.storeLink) return;
      window.open(this.storeLink, "_blank", "noopener,noreferrer");
    },

    redirectToGuild(guildId) {
      const server = this.servers.find((s) => s.guild_id === guildId);
      if (!server || !server.invite_link) return;

      window.open(server.invite_link, "_blank", "noopener,noreferrer");
      if (this.$ga) {
    const params = guildId ? guildId: "";

    let eventName = "click:button:server-discovery-join";
    this.$ga.event("serverDiscovery", eventName, params);
  }
    },
    formatMemberCount(count) {
      if (!count) return '0';
      if (count >= 1000000) {
        return Math.round(count / 1000000) + 'M';
      } else if (count >= 1000) {
        return Math.round(count / 1000) + 'K';
      }  else {
        return count;
      }
    },
  },
  computed: {
    paginationOptions() {
      if (this.totalServers === 1) {
        return { texts: { count: "1 record" } };
      }
      return {};
    },
    hasAnyFilter() {
      return (
        this.searchQuery.trim().length > 0 ||
        this.selectedCategory !== "" ||
        this.selectedTags.length > 0 ||
        this.showNsfw ||
        this.hideBoosted
      );
    },
  },
  watch: {
    currentPage(newPage) {
      this.onPageChange(newPage);
    },
  },
};
</script>

<style src="spinkit/spinkit.min.css"></style>

<style scoped>
/* Page Layout */
.browse-page-wrapper {
  color: #fff;
  padding-bottom: 3rem;
  width: 95%;
  margin: 0 auto;
}

.hero-section {
  text-align: center;
  padding: 2.5rem 1.5rem;
  margin-bottom: 0.5rem;
  border-radius: 16px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
  position: relative;
  overflow: hidden;
  background-color: #202225;
  background-image:
    radial-gradient(at 0% 0%, hsla(235, 86%, 65%, 0.15) 0px, transparent 50%),
    radial-gradient(
      at 100% 100%,
      hsla(340, 82%, 65%, 0.12) 0px,
      transparent 50%
    );
  border: 1px solid rgba(255, 255, 255, 0.05);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.hero-section::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px);
  background-size: 30px 30px;
  z-index: 0;
  pointer-events: none;
}

.hero-content {
  position: relative;
  z-index: 1;
}

.hero-title {
  font-size: 3.2rem;
  font-weight: 900;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #ffffff 0%, #b9bbbe 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  letter-spacing: -1px;
}

.hero-subtitle {
  font-size: 1.25rem;
  color: #b9bbbe;
  max-width: 600px;
  line-height: 1.6;
}

.filter-section {
  background: #202225;
  padding: 1.25rem 1.5rem;
  border-radius: 12px;
  margin-bottom: 0.75rem;
  border: 1px solid #2f3136;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Override input styling directly via global deep selector or inline scoped */
::v-deep .custom-select,
::v-deep .form-control {
  background-color: #2f3136 !important;
  border: 1px solid #202225 !important;
  color: #dcddde !important;
}
::v-deep .custom-select:focus,
::v-deep .form-control:focus {
  background-color: #40444b !important;
  border-color: #5865f2 !important;
  color: #fff !important;
  box-shadow: none !important;
}
::v-deep .custom-control-label::before {
  background-color: #2f3136;
  border-color: #202225;
}

/* Server Card */
.server-card {
  background-color: #202225 !important;
  border: 1px solid #2f3136 !important;
  border-radius: 12px !important;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    border-color 0.2s ease;
  overflow: hidden;
  position: relative;
}

.server-card.golden-hue {
  border: 1px solid #2f3136 !important;
  box-shadow: 0 4px 15px rgba(255, 170, 0, 0.05);
}

.server-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
}

.server-card.golden-hue:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(255, 170, 0, 0.2);
  border-color: #5c4e1a !important;
}

.server-card-content {
  display: flex;
  flex-direction: column;
  height: 100%;
}

/* Banner */
.server-banner-img {
  width: 100%;
  height: 180px;
  position: relative;
  overflow: hidden;
}

.nsfw-banner-badge {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 2;
  font-size: 0.7rem;
  padding: 0.3rem 0.6rem;
  border-radius: 4px;
  background-color: #ed4245 !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  font-weight: 700;
  letter-spacing: 0.5px;
}
.bumps-badges-wrap {
  position: absolute;
  bottom: 10px;
  right: 10px;
  /* .server-card-header overlaps the bottom of this banner (negative
     margin-top for the avatar-overlap effect) and also sits at z-index 2 -
     on a tie, DOM order wins and the header (which comes later) would
     intercept clicks here even though the badges are visually on top.
     Needs to stay above that. */
  z-index: 3;
  display: flex;
  align-items: center;
  gap: 6px;
}
.bumps-banner-badge {
  font-size: 0.75rem;
  padding: 0.3rem 0.7rem;
  border-radius: 6px;
  background-color: rgba(255, 255, 255, 0.15) !important;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  color: #ffffff;
  font-weight: 600;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}
.boosted-label-badge {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.7rem;
  padding: 0.3rem 0.6rem;
  border-radius: 6px;
  background: linear-gradient(135deg, #ffe066, #f5b500);
  color: #3a2a00;
  font-weight: 700;
  letter-spacing: 0.3px;
  white-space: nowrap;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
}
.boosted-label-icon {
  width: 12px;
  height: 12px;
  object-fit: contain;
  flex-shrink: 0;
}
.server-banner-bg {
  position: absolute;
  top: -10px;
  right: -10px;
  bottom: -10px;
  left: -10px;
  background-size: cover;
  background-position: center;
  filter: blur(8px);
  z-index: 0;
}
.server-banner-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 1;
}

/* Header */
.server-card-header {
  display: flex;
  align-items: flex-end;
  padding: 0 1.25rem;
  margin-top: -64px;
  margin-bottom: 1rem;
  flex-shrink: 0;
  position: relative;
  z-index: 2;
}

.server-avatar-wrapper {
  margin-right: 1rem;
  background: #202225;
  padding: 4px;
  border-radius: 50%;
  flex-shrink: 0;
  position: relative;
}

.avatar-member-badge {
  position: absolute;
  bottom: -4px;
  left: 50%;
  transform: translateX(-50%);
  background-color: #202225;
  border: 1px solid #5865f2;
  color: #b9bbbe;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.1rem 0.6rem;
  border-radius: 12px;
  display: flex;
  align-items: center;
  z-index: 3;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.4);
  white-space: nowrap;
}

.server-icon {
  width: 120px;
  height: 120px;
  object-fit: cover;
  border-radius: 50%;
}

.server-info {
  flex: 1;
  min-width: 0;
  padding-bottom: 0.1rem;
}

.server-name {
  font-size: 1.15rem;
  font-weight: 700;
  color: #ffffff;
  line-height: 1.2;
}

.category-badge {
  font-size: 0.75rem;
  padding: 0.25rem 0.6rem;
  align-self: flex-start;
  background-color: #5865f2 !important;
}

.member-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #57f287;
  box-shadow: 0 0 6px 1px rgba(87, 242, 135, 0.8), 0 0 12px rgba(87, 242, 135, 0.5);
  margin-right: 6px;
}

/* Description + Tags shared wrapper */
.server-desc-tags-wrapper {
  display: flex;
  flex-direction: column;
  height: 165px;
  overflow: hidden;
  padding-bottom: 0.5rem;
}

/* Tags */
.server-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 0 1.25rem;
  flex-shrink: 0;
  align-content: flex-start;
}

.tag-badge {
  font-size: 0.75rem;
  padding: 0.35rem 0.75rem;
  background-color: #2f3136 !important;
  color: #dcddde !important;
  border: 1px solid #40444b;
  max-height: 25px;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Description Area */
.server-description-area {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 0 1.25rem;
  min-height: 40px;
}

.server-description-area::-webkit-scrollbar {
  width: 5px;
}
.server-description-area::-webkit-scrollbar-track {
  background: transparent;
}
.server-description-area::-webkit-scrollbar-thumb {
  background-color: #40444b;
  border-radius: 10px;
}

.server-desc-text {
  font-size: 0.9rem;
  line-height: 1.45;
  color: #b9bbbe;
  white-space: pre-wrap;
  word-break: break-word;
}

/* Action Button */
.server-action {
  flex-shrink: 0;
  padding: 0 1.25rem 1.25rem 1.25rem;
}

.join-btn-premium {
  font-weight: 600;
  font-size: 0.95rem;
  letter-spacing: 0.5px;
  padding: 0.6rem;
  background-color: #3ba55d !important;
  border: none !important;
  transition: background-color 0.2s;
}

.join-btn-premium:hover {
  background-color: #2d8a4a !important;
}

.search-trigger-btn {
  height: 38px;
  background-color: #5865f2 !important;
  border-color: #5865f2 !important;
  font-weight: 600;
}

.search-trigger-btn:hover {
  background-color: #4752c4 !important;
  border-color: #4752c4 !important;
}

/* ===== MOBILE FIX: Keep side-by-side but prevent name overlapping banner ===== */
@media (max-width: 575.98px) {
  .server-card-header {
    margin-top: -40px;
    align-items: flex-end;
  }
  .server-icon {
    width: 80px;
    height: 80px;
  }
  .server-info {
    padding-top: 40px;
  }
}
</style>
